import React, { useEffect, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, IconButton, TextField, Typography, Collapse
} from '@mui/material';
import { ArrowBack, Add, ExpandMore, ExpandLess } from '@mui/icons-material';
import { api } from '../../../services/api';
import { toast } from 'react-toastify';

const MAX_PREIMPRESOS_POR_PEDIDO = 5000;

// Panel inline (no modal) para gestionar el pool de tickets preimpresos de una caja.
// Se accede desde el menú de la caja o al crear una caja preimpresa; reemplaza la lista.
const PreimpresosPanel = ({ eventoId, cajaId, onBack }) => {
  const [loading, setLoading] = useState(false);
  const [caja, setCaja] = useState(null);
  const [tarifas, setTarifas] = useState([]);
  const [cantidades, setCantidades] = useState({});
  const [generandoId, setGenerandoId] = useState(null);
  const [expandidas, setExpandidas] = useState({}); // { [idtarifa]: bool }

  const cargarCaja = async () => {
    if (!cajaId) return;
    setLoading(true);
    const res = await api.caja.getCaja(eventoId, cajaId);
    setLoading(false);

    if (res.status === "success") {
      setCaja(res.data);
      setTarifas((res.data?.tarifas || []).filter(t => t.preimpreso));
    } else {
      toast.error(res.message || "Error al obtener la caja");
    }
  };

  useEffect(() => {
    if (cajaId) {
      setCantidades({});
      cargarCaja();
    }
  }, [cajaId]);

  const handleGenerar = async (idtarifa) => {
    const cantidad = parseInt(cantidades[idtarifa], 10);
    if (!cantidad || cantidad <= 0) {
      toast.error("Ingresá una cantidad mayor a 0");
      return;
    }
    if (cantidad > MAX_PREIMPRESOS_POR_PEDIDO) {
      toast.error(`Se pueden generar hasta ${MAX_PREIMPRESOS_POR_PEDIDO} por vez`);
      return;
    }

    setGenerandoId(idtarifa);
    const res = await api.caja.generarPreimpresos(eventoId, cajaId, { idtarifa, cantidad });
    setGenerandoId(null);

    if (res.status === "success") {
      toast.success(res.message || "Tickets preimpresos generados");
      setCantidades(prev => ({ ...prev, [idtarifa]: "" }));
      cargarCaja();
    } else {
      toast.error(res.message || "Error al generar tickets preimpresos");
    }
  };

  const colorDot = (color) => {
    try {
      const c = JSON.parse(color);
      return `rgba(${c.r},${c.g},${c.b},${c.a})`;
    } catch { return "#c1c1c1"; }
  };

  return (
    <Box>
      {/* HEADER */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
        <IconButton onClick={onBack} size="small" sx={{ color: "#5656ff" }}>
          <ArrowBack />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography sx={{ fontSize: "18px", fontWeight: 600, color: "#0b0062" }}>
              {caja?.caja || "Caja"}
            </Typography>
            <Chip
              label="Preimpresa"
              size="small"
              sx={{ backgroundColor: "#e8e8ff", color: "#5656ff", fontWeight: 600, fontSize: "11px" }}
            />
          </Box>
          {caja?.usuario && (
            <Typography sx={{ fontSize: "13px", color: "#666" }}>{caja.usuario}</Typography>
          )}
        </Box>
      </Box>

      <Typography sx={{ fontSize: "13px", color: "#666", mb: 2 }}>
        Generá tickets por tarifa. Cada lote se suma al pool, con código, QR e imagen.
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : tarifas.length === 0 ? (
        <Box sx={{
          textAlign: 'center', py: 4, border: '1px dashed #ccc',
          borderRadius: 2, backgroundColor: '#f9f9f9'
        }}>
          <Typography color="text.secondary" sx={{ fontSize: "14px" }}>
            Esta caja no tiene tarifas preimpresas asignadas. Marcá la tarifa como preimpresa
            y asignala a la caja para poder generar tickets.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {tarifas.map(tarifa => {
            const preimpresos = tarifa.preimpresos || [];
            const abierta = !!expandidas[tarifa.id];
            return (
              <Box
                key={tarifa.id}
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: "12px",
                  p: 2,
                  backgroundColor: "#fff"
                }}
              >
                {/* TARIFA HEADER */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                  <span style={{ display: "inline-block", width: 14, height: 14, borderRadius: 4, background: colorDot(tarifa.color) }} />
                  <Typography sx={{ fontSize: "15px", fontWeight: 600, color: "#0b0062" }}>
                    {tarifa.concepto}
                  </Typography>
                  <Typography sx={{ ml: "auto", fontSize: "13px", color: "#666" }}>
                    ${tarifa.precio?.toLocaleString('es-AR')}
                  </Typography>
                </Box>

                {/* STATS */}
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mb: 1.5 }}>
                  <Box sx={{ backgroundColor: "#f5f5f7", borderRadius: "8px", p: 1.25 }}>
                    <Typography sx={{ fontSize: "12px", color: "#666" }}>Total</Typography>
                    <Typography sx={{ fontSize: "22px", fontWeight: 600, color: "#0b0062" }}>
                      {tarifa.total_preimpresos || 0}
                    </Typography>
                  </Box>
                  <Box sx={{ backgroundColor: "#e8f5e8", borderRadius: "8px", p: 1.25 }}>
                    <Typography sx={{ fontSize: "12px", color: "#2e7d32" }}>Disponibles</Typography>
                    <Typography sx={{ fontSize: "22px", fontWeight: 600, color: "#2e7d32" }}>
                      {tarifa.restantes_preimpresos || 0}
                    </Typography>
                  </Box>
                </Box>

                {/* GENERAR */}
                <Box sx={{ display: "flex", gap: 1 }}>
                  <TextField
                    type="number"
                    size="small"
                    placeholder="Cantidad"
                    value={cantidades[tarifa.id] || ""}
                    onChange={(e) => setCantidades(prev => ({ ...prev, [tarifa.id]: e.target.value }))}
                    inputProps={{ min: 1 }}
                    sx={{ flex: 1 }}
                  />
                  <Button
                    variant="contained"
                    startIcon={generandoId === tarifa.id ? null : <Add />}
                    onClick={() => handleGenerar(tarifa.id)}
                    disabled={generandoId === tarifa.id}
                    sx={{
                      flex: 1,
                      backgroundColor: "#5656FF",
                      color: "white",
                      textTransform: "none",
                      "&:hover": { backgroundColor: "#4545dd" }
                    }}
                  >
                    {generandoId === tarifa.id ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Generar"}
                  </Button>
                </Box>

                {/* LISTA DE TICKETS */}
                {preimpresos.length > 0 && (
                  <Box sx={{ mt: 1.5 }}>
                    <Button
                      onClick={() => setExpandidas(prev => ({ ...prev, [tarifa.id]: !abierta }))}
                      endIcon={abierta ? <ExpandLess /> : <ExpandMore />}
                      sx={{ textTransform: "none", color: "#5656ff", fontSize: "13px", p: 0 }}
                    >
                      {abierta ? "Ocultar tickets" : `Ver tickets (${preimpresos.length})`}
                    </Button>
                    <Collapse in={abierta}>
                      <Box sx={{
                        mt: 1, maxHeight: 220, overflowY: "auto",
                        border: "1px solid #f0f0f0", borderRadius: "8px"
                      }}>
                        {preimpresos.map(t => {
                          const disponible = !t.idcompra || t.idcompra === 0;
                          return (
                            <Box
                              key={t.id}
                              sx={{
                                display: "flex", alignItems: "center", justifyContent: "space-between",
                                px: 1.25, py: 0.75, borderBottom: "1px solid #f5f5f5",
                                "&:last-child": { borderBottom: "none" }
                              }}
                            >
                              <Typography sx={{ fontSize: "13px", fontFamily: "monospace", color: "#333" }}>
                                #{t.id} · {t.codigo}
                              </Typography>
                              <Chip
                                label={disponible ? "Disponible" : "Vendido"}
                                size="small"
                                sx={{
                                  height: 20, fontSize: "11px", fontWeight: 600,
                                  backgroundColor: disponible ? "#e8f5e8" : "#eee",
                                  color: disponible ? "#2e7d32" : "#888"
                                }}
                              />
                            </Box>
                          );
                        })}
                      </Box>
                    </Collapse>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default PreimpresosPanel;
